import { useEffect, useRef } from 'react'
import { useEditor } from '@/state/store'
import { localProvider } from '@/storage/local'
import { migrateProject } from '@/model/migrate'
import { hasShareHash } from '@/lib/share'

/**
 * Debounced autosave of the current project to IndexedDB whenever it changes.
 * Also loads the most-recent project on first mount (if any).
 *
 * Share-link aware (Track 7a): when the page is opened on a `#d=` share link, the
 * shared design is loaded read-only by `useShareLink` and we must NOT (a) load the
 * user's local project over it, nor (b) save the shared design into their local
 * store. Only the normal (no-hash) boot path loads the most-recent local project,
 * and saving is gated on `!readOnly` so a Fork (which clears readOnly) persists the
 * new copy while plain viewing never clobbers local work.
 */
export function useAutosave(delay = 600) {
  const project = useEditor((s) => s.project)
  const readOnly = useEditor((s) => s.readOnly)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loaded = useRef(false)

  // Load the most recent project once on mount — unless we're viewing a share.
  useEffect(() => {
    if (hasShareHash()) {
      // useShareLink owns boot in this case; don't touch local storage.
      loaded.current = true
      return
    }
    let cancelled = false
    ;(async () => {
      const list = await localProvider.list()
      if (cancelled || list.length === 0) {
        loaded.current = true
        return
      }
      const recent = await localProvider.load(list[0].id)
      if (!cancelled && recent) {
        useEditor.getState().loadProject(migrateProject(recent))
      }
      loaded.current = true
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Persist on change (debounced). Never while viewing a shared (read-only) design.
  useEffect(() => {
    if (!loaded.current || readOnly) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      void localProvider.save(project)
    }, delay)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [project, readOnly, delay])
}
